import { LoadingButton } from '@mui/lab';
import {
  Box,
  Card,
  Checkbox,
  Chip,
  FormControl,
  FormControlLabel,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  Typography,
} from '@mui/material';
import { IconButton, InputAdornment } from '@mui/material';
import ColorizeRoundedIcon from '@mui/icons-material/ColorizeRounded';
import axios from 'axios';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import ConfirmationDialog from 'src/components/ConfirmationDialog';
import { FormProvider, RHFTextField, RHFUploadAvatar } from 'src/components/hook-form';
import LinearProgressBar from 'src/components/progress-bar/progressBar.component';
import HttpServices from 'src/services/httpService';
import { imageModeuleUrl, userModuleURL } from 'src/services/urlService';
import { CommonHelper } from 'src/utils/commonHelper';
import { useToaster } from 'src/utils/toaster/toasterContext';
import ValidationTool from 'src/utils/validationHelper';
import ColorPopover from '../branding/branding-color-popover';

const getAIKeySaveState = (value, isChanged) => {
  const trimmedValue = String(value || '').trim();
  if (!isChanged || trimmedValue.includes('*')) {
    return { shouldUpdate: false, value: '' };
  }
  return { shouldUpdate: true, value: trimmedValue };
};

const OrgInfoComponent = () => {
  const { t: translate } = useTranslation();
  const { showToast } = useToaster();
  const navigate = useNavigate();
  const maxFileSizeInBytes = 10485760;
  const isUserHasPermission = CommonHelper.CheckForPermissionAvailableForUser();
  const userOrgDetails = CommonHelper.GetBrandInfoFromLocalStorage();
  const isCustomDomain = CommonHelper.CustomDomain();

  //State Variables

  const [orgPrimaryColor, setOrgPrimaryColor] = useState('');
  const [orgSecondaryColor, setOrgSecondaryColor] = useState('');
  const [orgName, setOrgName] = useState('');
  const [imageUrl, setOrgImage] = useState('');
  const [is24HourFormat, setIs24HourFormat] = useState(false);
  const [openAIAPIKey, setOpenAIAPIKey] = useState('');
  const [hasOpenAIAPIKey, setHasOpenAIAPIKey] = useState(false);
  const [geminiAPIKey, setGeminiAPIKey] = useState('');
  const [hasGeminiAPIKey, setHasGeminiAPIKey] = useState(false);
  const [isLeaveOrgLoading, setLeaveOrgLoader] = useState(false);
  const [isDeleteOrgLoading, setDeleteOrgLoader] = useState(false);
  const [orgDescription, setOrgDescription] = useState('');
  const [formSchema] = useState({});
  const [listOrgAdmins, setListOrgAdmins] = useState([]);
  const [formValidationErrors, setFormValidationErrors] = useState({});
  const [isSaveChangesLoading, setSaveChangesLoader] = useState(false);
  const [fileUploadprogress, setFileUploadprogress] = useState(0);
  const [selectedAdmin, setSelectedAdmin] = useState('');
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [currentAction, setCurrentAction] = useState(null);
  const [popoverElement, setPopoverElement] = useState(null);
  const [selectedPopOverData, setSelectedPopOverData] = useState('');
  useEffect(() => {
    getOrganizationInfo();
    getAllOrganizationAdmins();
  }, []);

  useEffect(() => {}, [imageUrl]);

  const getOrganizationInfo = () => {
    let getProps = {
      url: userModuleURL.fetchOrgInfo,
      successCallback: brandSuccessCallBack,
      failureCallback,
    };
    HttpServices.Get(getProps);
  };
  const getAllOrganizationAdmins = () => {
    let getProps = {
      url: userModuleURL.fetchOrgAdmins,
      successCallback: fetchOrgAdminsSuccessCallBack,
      failureCallback,
    };
    HttpServices.Get(getProps);
  };
  const fetchOrgAdminsSuccessCallBack = (data, message) => {
    setListOrgAdmins(data);

    // Find the admin with is_portal_admin: true and set as selected
    const defaultAdmin = data.find((admin) => admin.is_portal_admin);
    if (defaultAdmin) {
      setSelectedAdmin(defaultAdmin.user_id); // Set the selected admin user_id as default
    }
  };
  const brandSuccessCallBack = (data, message) => {
    setOrgName(data.org_name);
    setOrgImage(data.org_icon_url);
    setOrgDescription(data.org_description);
    setIs24HourFormat(data.is_24_hour_format);
    setOrgPrimaryColor(data.org_primary_color);
    setOrgSecondaryColor(data.org_secondary_color);
    if (data.has_openai_api_key) setOpenAIAPIKey('**************');
    setHasOpenAIAPIKey(false);
    if (data.has_gemini_api_key) setGeminiAPIKey('**************');
    setHasGeminiAPIKey(false);
  };
  const successCallback = (data, message) => {
    showToast(message, 's');
    setSaveChangesLoader(false);
    window.location.reload();
  };
  const failureCallback = (message) => {
    showToast(message, 'e');
    setSaveChangesLoader(false);
    setDeleteOrgLoader(false);
    setLeaveOrgLoader(false);
  };
  const handleSelectChange = (event) => {
    setSelectedAdmin(event.target.value);
  };

  const handleSubmitForm = () => {
    let userForm = {
      orgName: orgName,
    };
    let { formValidationErrors, isValidForm } = ValidationTool.FormValidation(formSchema, userForm);
    if (isValidForm) {
      setFormValidationErrors({});
      onSubmit();
    } else setFormValidationErrors(formValidationErrors);
  };
  const deleteOrgSuccessCallback = (data, message) => {
    CommonHelper.ClearAppLocalStorage();
    // navigate('/no-organization');
    window.location.reload();
  };
  const handleOpenConfirm = (action) => {
    setCurrentAction(action);
    setConfirmOpen(true);
  };

  const handleCloseConfirm = () => {
    setConfirmOpen(false);
    setCurrentAction(null);
  };

  const handleConfirmAction = () => {
    if (currentAction === 'delete') {
      handleDeleteOrg();
    } else if (currentAction === 'remove') {
      handleLeaveOrg();
    }
    handleCloseConfirm(); // Close the confirmation dialog after confirming action
  };
  const handleDeleteOrg = () => {
    setDeleteOrgLoader(true);
    CommonHelper.DeleteOrganization(deleteOrgSuccessCallback, failureCallback);
  };
  const leaveOrgSuccessCallback = (data, message) => {
    navigate('/no-organization', { replace: true });
  };
  const handleLeaveOrg = () => {
    setLeaveOrgLoader(true);
    let postProps = {
      url: userModuleURL.leaveOrganization,
      successCallback: leaveOrgSuccessCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };
  const onSubmit = () => {
    const openAIKeySave = getAIKeySaveState(openAIAPIKey, hasOpenAIAPIKey);
    const geminiKeySave = getAIKeySaveState(geminiAPIKey, hasGeminiAPIKey);
    let brandProps = {
      org_name: orgName,
      org_icon_url: imageUrl,
      org_description: orgDescription,
      portal_owner_user_id: selectedAdmin,
      is_24_hour_format: is24HourFormat,
      openai_api_key: openAIKeySave.value,
      has_openai_api_key: openAIKeySave.shouldUpdate,
      gemini_api_key: geminiKeySave.value,
      has_gemini_api_key: geminiKeySave.shouldUpdate,
      org_primary_color: orgPrimaryColor,
      org_secondary_color: orgSecondaryColor,
    };
    setSaveChangesLoader(true);
    let postProps = {
      url: userModuleURL.updateOrg,
      body: brandProps,
      successCallback,
      failureCallback,
    };
    HttpServices.Post(postProps);
  };
  const handleDrop = (acceptedFiles) => {
    if (acceptedFiles?.length > 0) {
      const file = acceptedFiles[0];
      handleFileUpload(file);
    } else {
      showToast(
        translate(
          'organization_info.unsupported_format',
          'We only support JPEG, and PNG file formats. The selected file are not in the supported format'
        ),
        'e'
      );
    }
  };
  const fileUploadSuccessCallback = (data, message) => {
    setOrgImage(data.media_data[0].media_url);
  };
  const handleFileUpload = (file) => {
    let isFileInCorrectFormat = CommonHelper.SupportedImageFormat.some((a) => a.includes(file.type));
    if (isFileInCorrectFormat) {
      if (file.size < maxFileSizeInBytes) {
        setFileUploadprogress(1);
        let formData = new FormData();
        formData.append('fileupload', file);
        axios
          .post(imageModeuleUrl.uploadMedia, formData, {
            headers: HttpServices.GetRequestHeader('form', true),
            onUploadProgress: (data) => {
              let loadedProgress = Math.round((100 * data.loaded) / data.total);
              setFileUploadprogress(loadedProgress > 90 ? 90 : loadedProgress);
            },
          })
          .then((result) => result)
          .then((response) => {
            setFileUploadprogress(100);
            if (response.status === 200 && response.data.http_code === 200)
              fileUploadSuccessCallback(
                response.data.data,
                translate('organization_info.file_uploaded_success', 'File Uploaded Successfully')
              );
            else
              showToast(
                translate(
                  'organization_info.something_went_wrong',
                  'Something went wrong. Please contact our technical team'
                ),
                'e'
              );
            setFileUploadprogress(0);
          })
          .catch((error) => {
            setFileUploadprogress(0);
            showToast(error.toString(), 'e');
          });
      } else
        showToast(
          translate(
            'organization_info.max_file_size',
            'Maximum file size for image is 10MB. Please select image below 10 MB'
          ),
          'e'
        );
    } else {
      showToast(
        translate(
          'organization_info.unsupported_format',
          'We only support JPEG, and PNG file formats. The selected file are not in the supported format'
        ),
        'e'
      );
    }
  };
  const handleInputChange = (event) => {
    let value = event.target.value;
    let name = event.target.name;
    switch (name) {
      case 'orgName':
        setOrgName(value);
        break;
      case 'orgDescription':
        setOrgDescription(value);
        break;
      case 'openAIAPIKey':
        setHasOpenAIAPIKey(true);
        setOpenAIAPIKey(value?.includes('*') ? '' : value);
        break;
      case 'geminiAPIKey':
        setHasGeminiAPIKey(true);
        setGeminiAPIKey(value?.includes('*') ? '' : value);
        break;
      case 'orgPrimaryColor':
        value = value ? value.replaceAll(' ', '') : '';
        value = formatHexColor(value);
        setOrgPrimaryColor(value);
        break;
      case 'orgSecondaryColor':
        value = value ? value.replaceAll(' ', '') : '';
        value = formatHexColor(value);
        setOrgSecondaryColor(value);
        break;
      default:
        break;
    }
  };
  const formatHexColor = (value) => {
    if (!value) return '';

    // Remove all # symbols first
    let cleaned = value.replaceAll('#', '');

    // Remove any non-hex characters (only allow 0-9, a-f, A-F)
    cleaned = cleaned.replace(/[^0-9a-fA-F]/g, '');

    // Limit to 6 characters max
    cleaned = cleaned.slice(0, 6);

    // Only add # if there's at least 1 valid character
    return cleaned.length > 0 ? `#${cleaned}` : '';
  };
  const handleCloseMenu = () => {
    setPopoverElement(null);
  };

  const handleColor = (color, type) => {
    const formattedColor = formatHexColor(color.hex);
    if (type === 'primary') setOrgPrimaryColor(formattedColor);
    else setOrgSecondaryColor(formattedColor);
  };

  const brandActionProps = {
    popoverElement: popoverElement,
    selectedPopOverData: selectedPopOverData,
    handleCloseMenu,
    handleColor,
    color: orgPrimaryColor,
  };
  const handleOpenMenu = (event, data) => {
    setSelectedPopOverData(data);
    setPopoverElement(event.currentTarget);
  };
  const defaultValues = {
    orgName: orgName || '',
    imageUrl: imageUrl || '',
    orgDescription: orgDescription || '',
  };
  const methods = useForm({
    defaultValues,
  });
  const { handleSubmit } = methods;
  const handleDeleteOrgImage = () => {
    setOrgImage('');
  };
  return (
    <>
      <FormProvider methods={methods} onSubmit={handleSubmit(handleSubmitForm)}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={4}>
            <Box display="flex" flexDirection="column" height="100%">
              <Card sx={{ pt: 10, pb: 5, px: 3, textAlign: 'center', flexGrow: 1 }}>
                <Box position="relative" display="inline-block">
                  <RHFUploadAvatar
                    name="imageUrl"
                    accept="image/*"
                    onDrop={handleDrop}
                    handleDeleteProfiles={handleDeleteOrgImage}
                    url={imageUrl}
                    helperText={
                      <Typography
                        variant="caption"
                        sx={{
                          mt: 2,
                          mx: 'auto',
                          display: 'block',
                          textAlign: 'center',
                          color: 'text.secondary',
                        }}
                      >
                        Allowed *.jpeg, *.jpg, *.png, *.gif
                        <br /> max size of 5Mb
                      </Typography>
                    }
                  />
                </Box>
                <Stack alignItems="center" justifyContent="center" spacing={2}>
                  {!isCustomDomain && (
                    <LoadingButton
                      variant="contained"
                      loading={isLeaveOrgLoading}
                      onClick={() => {
                        handleOpenConfirm('remove');
                      }}
                    >
                      Leave Organization
                    </LoadingButton>
                  )}
                  {isUserHasPermission &&
                    userOrgDetails.is_portal_admin &&
                    !userOrgDetails?.current_plan_info?.is_appsumo_user && (
                      <LoadingButton
                        variant="contained"
                        loading={isDeleteOrgLoading}
                        onClick={() => {
                          handleOpenConfirm('delete');
                        }}
                      >
                        Delete Organization
                      </LoadingButton>
                    )}
                </Stack>
                <ConfirmationDialog
                  open={confirmOpen}
                  onClose={handleCloseConfirm}
                  onConfirm={handleConfirmAction}
                  title={`Confirm ${currentAction === 'delete' ? 'Deletion' : 'Leave'}`}
                  message={`Are you sure you want to ${
                    currentAction === 'delete' ? 'delete this organization' : 'Leave this organization'
                  }? This action cannot be undone`}
                  submitButtonName="Yes"
                />
                {fileUploadprogress > 0 && <LinearProgressBar value={fileUploadprogress} />}
              </Card>
            </Box>
          </Grid>
          <Grid item xs={12} md={8}>
            <Box display="flex" flexDirection="column" height="100%">
              <Card sx={{ p: 3, flexGrow: 1 }}>
                <Box
                  sx={{
                    display: 'grid',
                    rowGap: 3,
                    columnGap: 2,
                    gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                  }}
                >
                  <RHFTextField
                    name="orgName"
                    label="Org Name"
                    value={orgName}
                    onChange={handleInputChange}
                    error={formValidationErrors?.orgName}
                    helperText={formValidationErrors?.orgName}
                  />
                  <Box
                    sx={{
                      mt: 0,
                      p: 2,
                      border: '1px solid #eee',
                      borderRadius: 2,
                      gridColumn: { xs: '1', sm: '1 / span 2' },
                    }}
                  >
                    <Typography variant="subtitle1" sx={{ mb: 2 }}>
                      AI Chat Keys
                    </Typography>
                    <Box
                      sx={{
                        display: 'grid',
                        rowGap: 3,
                        columnGap: 2,
                        gridTemplateColumns: { xs: 'repeat(1, 1fr)', sm: 'repeat(2, 1fr)' },
                      }}
                    >
                      <RHFTextField
                        name="openAIAPIKey"
                        label={hasOpenAIAPIKey || openAIAPIKey?.includes('*') ? 'OpenAI API Key configured' : 'OpenAI API Key'}
                        value={openAIAPIKey}
                        onChange={handleInputChange}
                      />
                      <RHFTextField
                        name="geminiAPIKey"
                        label={hasGeminiAPIKey || geminiAPIKey?.includes('*') ? 'Gemini API Key configured' : 'Gemini API Key'}
                        value={geminiAPIKey}
                        onChange={handleInputChange}
                      />
                    </Box>
                  </Box>
                  <FormControl fullWidth sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}>
                    <InputLabel id="org-admin-select-label">Select Admin</InputLabel>
                    <Select
                      labelId="org-admin-select-label"
                      value={selectedAdmin}
                      onChange={handleSelectChange}
                      fullWidth
                      label={translate('common.select_admin', 'Select Admin')}
                      displayEmpty
                    >
                      {listOrgAdmins.map((admin) => (
                        <MenuItem key={admin.user_id} value={admin.user_id} disabled={!admin.is_enabled}>
                          <Stack direction="row" alignItems="center" spacing={1}>
                            <Typography>{`${admin.first_name} (${admin.email})`}</Typography>
                            {admin.is_portal_admin && (
                              <Chip
                                label={translate('common.portal_admin', 'Portal Admin')}
                                color="primary"
                                size="small"
                              />
                            )}
                          </Stack>
                        </MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                  <RHFTextField
                    name="orgPrimaryColor"
                    value={orgPrimaryColor}
                    label={translate('manage_branding.branding.organization_primary_color')}
                    onChange={handleInputChange}
                    error={formValidationErrors?.orgPrimaryColor}
                    helperText={`${translate('manage_branding.branding.enter_hex_code')} ${translate(
                      'manage_branding.branding.for_example'
                    )} #ffffff`}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="large"
                            color="inherit"
                            onClick={(e) => {
                              handleOpenMenu(e, 'primary');
                            }}
                          >
                            <ColorizeRoundedIcon />
                          </IconButton>
                          {orgPrimaryColor && (
                            <Box
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                boxShadow: 1,
                                backgroundColor: orgPrimaryColor,
                                marginLeft: 1,
                              }}
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />

                  <RHFTextField
                    name="orgSecondaryColor"
                    value={orgSecondaryColor}
                    label={translate('manage_branding.branding.organization_secondary_color')}
                    onChange={handleInputChange}
                    error={formValidationErrors?.orgSecondaryColor}
                    helperText={`${translate('manage_branding.branding.enter_hex_code')} ${translate(
                      'manage_branding.branding.for_example'
                    )} #000000`}
                    InputProps={{
                      endAdornment: (
                        <InputAdornment position="end">
                          <IconButton
                            size="large"
                            color="green"
                            onClick={(e) => {
                              handleOpenMenu(e, 'secondary');
                            }}
                          >
                            <ColorizeRoundedIcon />
                          </IconButton>
                          {orgSecondaryColor && (
                            <Box
                              style={{
                                width: 24,
                                height: 24,
                                borderRadius: '50%',
                                boxShadow: 1,
                                backgroundColor: orgSecondaryColor,
                                marginLeft: 1,
                              }}
                            />
                          )}
                        </InputAdornment>
                      ),
                    }}
                  />
                  <RHFTextField
                    name="orgDescription"
                    multiline
                    rows={4}
                    label="Organization Description"
                    value={orgDescription}
                    onChange={handleInputChange}
                    sx={{ gridColumn: { xs: '1', sm: '1 / span 2' } }}
                  />
                </Box>
                {isUserHasPermission && userOrgDetails.is_portal_admin && (
                  <Stack alignItems="space-between">
                    <Stack spacing={3} direction="row" justifyContent="space-between" sx={{ mt: 3 }}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={is24HourFormat}
                            onChange={(e) => setIs24HourFormat(e.target.checked)}
                            name="24HourFormat"
                            color="primary"
                          />
                        }
                        label="Use 24-hour time format"
                        sx={{ gridColumn: '1 / -1' }}
                      />
                      {/* <FormControlLabel
                        control={
                          <Checkbox
                            checked={isReseller}
                            disabled={isReseller}
                            onChange={(e) => e.target.checked && setIsResellerInfoPopupOpen(true)}
                            name="isReseller"
                            color="primary"
                          />
                        }
                        label="Is Reseller"
                        sx={{ gridColumn: '1 / -1' }}
                      /> */}
                      <LoadingButton type="submit" variant="contained" loading={isSaveChangesLoading}>
                        Save Changes
                      </LoadingButton>
                    </Stack>
                  </Stack>
                )}
              </Card>
            </Box>
          </Grid>
        </Grid>
      </FormProvider>
      <ColorPopover {...brandActionProps} />
    </>
  );
};
export default OrgInfoComponent;

